import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileDown, Calendar as CalendarIcon, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { createLog } from '@/utils/createLog';

export default function Reports() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [projects, setProjects] = useState([]);
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setMonth(new Date().getMonth() - 1)), // Last month by default
        to: new Date()
    });
    const [selectedProject, setSelectedProject] = useState("all");

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
            setUser(storedUser);
            const isAuthorized = storedUser.role === 'admin' || storedUser.role === 'super_admin' || storedUser.role === 'superadmin';
            if (!isAuthorized) {
                createLog({
                    ticket: { id: "N/A", ticketNumber: "N/A" },
                    action: "unauthorized_access",
                    message: `User attempted to access unauthorized page: Reports`,
                    user: storedUser,
                });
                alert("You do not have access to this page");
                navigate("/dashboard", { replace: true });
                return;
            }
            fetchData(storedUser);
        }
    }, [navigate]);

    const fetchData = async (currentUser) => {
        setLoading(true);
        try {
            // Fetch Projects
            const projectsQuery = query(collection(db, 'projects'));
            const projectsSnap = await getDocs(projectsQuery);
            const projectsList = projectsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setProjects(projectsList);

            // Fetch Tickets based on Role
            let ticketsQuery;
            const ticketsRef = collection(db, 'tickets');

            if (currentUser.role === 'admin' || currentUser.role === 'super_admin') {
                ticketsQuery = query(ticketsRef, orderBy('created_at', 'desc'));
            } else if (currentUser.role === 'developer') {
                ticketsQuery = query(ticketsRef, where('assigned_developer', '==', currentUser.uid), orderBy('created_at', 'desc'));
            } else if (currentUser.role === 'client') {
                // For clients, we might need a more complex query (tickets created by them OR in their projects)
                // For simplified reports, let's start with tickets created by them
                ticketsQuery = query(ticketsRef, where('created_by', '==', currentUser.uid), orderBy('created_at', 'desc'));
            } else {
                ticketsQuery = query(ticketsRef, orderBy('created_at', 'desc')); // Default fallback (maybe empty?)
            }

            const ticketsSnap = await getDocs(ticketsQuery);
            const ticketsList = ticketsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert timestamp to Date object for easier filtering
                createdDate: doc.data().created_at?.toDate() || new Date()
            }));
            setTickets(ticketsList);

        } catch (error) {
            console.error("Error fetching report data:", error);
            toast.error("Failed to load report data");
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        return tickets.filter(ticket => {
            const ticketDate = ticket.createdDate;
            const isWithinDate = (!dateRange.from || ticketDate >= dateRange.from) &&
                (!dateRange.to || ticketDate <= new Date(dateRange.to.setHours(23, 59, 59, 999)));

            const isProjectMatch = selectedProject === "all" || ticket.project_id === selectedProject;

            return isWithinDate && isProjectMatch;
        });
    }, [tickets, dateRange, selectedProject]);

    const projectStats = useMemo(() => {
        const stats = {};

        // Initialize stats for all projects (even those with no tickets in range)
        projects.forEach(project => {
            stats[project.id] = {
                id: project.id,
                name: project.name,
                total: 0,
                open: 0,
                inProgress: 0,
                closed: 0,
                highPriority: 0
            };
        });

        // Populate stats from filtered tickets
        filteredData.forEach(ticket => {
            const projectId = ticket.project_id;
            if (!stats[projectId]) {
                // Handle tickets with deleted properties or unknown projects
                const unknownProjectId = 'unknown';
                if (!stats[unknownProjectId]) {
                    stats[unknownProjectId] = {
                        id: 'unknown',
                        name: 'Unknown Project',
                        total: 0, open: 0, inProgress: 0, closed: 0, highPriority: 0
                    };
                }
                stats[unknownProjectId].total++;
                // ... (add logic for unknown if needed, but for now stick to known projects)
                return;
            }

            const projectStat = stats[projectId];
            projectStat.total++;

            const status = ticket.status?.toLowerCase() || '';
            if (status === 'open' || status === 'todo') projectStat.open++;
            else if (status === 'in progress' || status === 'inprogress') projectStat.inProgress++;
            else if (['closed', 'done', 'completed', 'resolved', 'deployed'].includes(status)) projectStat.closed++;

            if (ticket.priority === 'High') projectStat.highPriority++;
        });

        return Object.values(stats).filter(s => selectedProject === "all" || s.id === selectedProject);
    }, [filteredData, projects, selectedProject]);

    const handleExportExcel = () => {
        try {
            // 1. Prepare Summary Data
            const summaryData = projectStats.map(stat => ({
                'Project Name': stat.name,
                'Total Tickets': stat.total,
                'Open Tickets': stat.open,
                'In Progress': stat.inProgress,
                'Closed Tickets': stat.closed,
                'High Priority': stat.highPriority
            }));

            // 2. Prepare Detailed Ticket Data
            const detailedData = filteredData.map(ticket => {
                const project = projects.find(p => p.id === ticket.project_id);
                return {
                    'Ticket ID': ticket.ticketNumber || ticket.id,
                    'Title': ticket.title,
                    'Project': project?.name || 'Unknown',
                    'Status': ticket.status,
                    'Priority': ticket.priority,
                    'Type': ticket.type,
                    'Assigned To': ticket.assigned_developer_name || 'Unassigned',
                    'Created By': ticket.created_by_name || 'Unknown',
                    'Created Date': format(ticket.createdDate, 'yyyy-MM-dd HH:mm'),
                    'Due Date': ticket.due_date ? format(new Date(ticket.due_date), 'yyyy-MM-dd') : 'N/A'
                };
            });

            // 3. Create Workbook
            const wb = XLSX.utils.book_new();

            const wsSummary = XLSX.utils.json_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, wsSummary, "Project Summary");

            const wsDetails = XLSX.utils.json_to_sheet(detailedData);
            XLSX.utils.book_append_sheet(wb, wsDetails, "Ticket Details");

            // 4. Save File
            const fileName = `Ticket_Report_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`;
            XLSX.writeFile(wb, fileName);

            toast.success("Report downloaded successfully");
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Failed to export report");
        }
    };

    return (
        <Layout user={user}>
            <div className="space-y-6 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-5">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Project Reports</h1>
                        <p className="text-muted-foreground mt-1">
                            View and export project-wise ticket statistics
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleExportExcel} className="gap-2">
                            <FileDown className="h-4 w-4" />
                            Export Excel
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="space-y-2 flex-1">
                                <label className="text-sm font-medium">Date Range</label>
                                <div className="flex gap-2 items-center">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange.from && "text-muted-foreground")}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {dateRange.from ? format(dateRange.from, "PPP") : <span>Pick a start date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={dateRange.from}
                                                onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <span className="text-muted-foreground">-</span>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange.to && "text-muted-foreground")}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {dateRange.to ? format(dateRange.to, "PPP") : <span>Pick an end date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={dateRange.to}
                                                onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>

                            <div className="space-y-2 flex-1">
                                <label className="text-sm font-medium">Project</label>
                                <Select value={selectedProject} onValueChange={setSelectedProject}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Project" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Projects</SelectItem>
                                        {projects.map(p => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="pb-0.5">
                                <Button variant="ghost" onClick={() => {
                                    setSelectedProject("all");
                                    setDateRange({
                                        from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
                                        to: new Date()
                                    });
                                }}>
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Summary Cards */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
                            <Filter className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{filteredData.length}</div>
                            <p className="text-xs text-muted-foreground">In selected range</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
                            <div className="h-4 w-4 rounded-full bg-red-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {filteredData.filter(t => t.priority === 'High').length}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Open / To Do</CardTitle>
                            <div className="h-4 w-4 rounded-full bg-yellow-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {filteredData.filter(t => ['open', 'todo'].includes(t.status?.toLowerCase())).length}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Completed</CardTitle>
                            <div className="h-4 w-4 rounded-full bg-green-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {filteredData.filter(t => ['closed', 'done', 'completed', 'resolved', 'deployed'].includes(t.status?.toLowerCase())).length}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Project Breakdown Table */}
                <Card className="col-span-4">
                    <CardHeader>
                        <CardTitle>Project Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="py-10 text-center text-muted-foreground">Loading reports...</div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Project Name</TableHead>
                                        <TableHead>Total Tickets</TableHead>
                                        <TableHead>Open</TableHead>
                                        <TableHead>In Progress</TableHead>
                                        <TableHead>Closed</TableHead>
                                        <TableHead>High Priority</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {projectStats.map(stat => (
                                        stat.total > 0 && (
                                            <TableRow key={stat.id}>
                                                <TableCell className="font-medium">{stat.name}</TableCell>
                                                <TableCell>{stat.total}</TableCell>
                                                <TableCell className="text-yellow-600 font-medium">{stat.open}</TableCell>
                                                <TableCell className="text-blue-600 font-medium">{stat.inProgress}</TableCell>
                                                <TableCell className="text-green-600 font-medium">{stat.closed}</TableCell>
                                                <TableCell className="text-red-600 font-medium">{stat.highPriority}</TableCell>
                                            </TableRow>
                                        )
                                    ))}
                                    {projectStats.every(s => s.total === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center h-24 text-muted-foreground">
                                                No tickets found for this selection
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
